import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Receipt, Download, Search } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
  StatusCell,
  MoneyCell,
  DateCell,
  ActionsMenuCell,
  type StatusTone,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useInvoices, type Invoice, type InvoiceStatus } from '@/domains/billing';

const STATUS_TONE: Record<InvoiceStatus, StatusTone> = {
  draft: 'draft',
  unpaid: 'pending',
  overdue: 'overdue',
  paid: 'paid',
  cancelled: 'draft',
  refunded: 'draft',
};

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
  paid: 'Paid',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unpaid', label: 'Unpaid' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'paid', label: 'Paid' },
] as const;

type FilterKey = (typeof FILTERS)[number]['key'];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useInvoices();
  const [filter, setFilter] = useState<FilterKey>('all');

  /**
   * A customer's own billing history — a FLOW set, so the period is meaningful
   * and is labelled `Issued`. `dateMode: 'client'` under AR-5 option (a):
   * `useInvoices()` fetches this customer's whole set with no LIMIT, and one
   * customer's book is a handful of documents.
   *
   * Search also covers `quoteNumber`, which the old two-line filter did not —
   * a customer who was quoted QUO-2026-000047 and then paid it could not find
   * the document by the number on the quotation they were sent, because the row
   * carries the INV number after conversion (the quotation model converts in
   * place). That was a real dead end, not a cosmetic gap.
   */
  const table = useTableFilters<Invoice>({
    rows: data,
    dateMode: 'client',
    dateLabel: 'Issued',
    dateOf: (inv) => inv.issuedAt,
    searchText: (inv) => [inv.number, inv.quoteNumber ?? ''].join(' '),
    facets: useMemo(
      () => [
        (inv: Invoice) =>
          filter === 'all' ||
          (filter === 'unpaid'
            ? inv.status === 'unpaid' || inv.status === 'overdue'
            : inv.status === filter),
      ],
      [filter],
    ),
  });

  const visible = table.rows;

  const columns: UniversalTableColumn<Invoice>[] = [
    {
      key: 'number',
      header: 'Document',
      sortValue: (i) => i.number,
      render: (i) => (
        <span className="inline-flex items-center gap-2">
          <span className="text-sm font-semibold tabular-nums text-ink">
            {i.number}
          </span>
          {i.docType === 'quotation' && (
            <span className="rounded bg-warning/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-dark">
              Quotation
            </span>
          )}
        </span>
      ),
      width: 'w-44',
    },
    {
      key: 'issuedAt',
      header: 'Issued',
      sortValue: (i) => i.issuedAt,
      render: (i) => <DateCell value={i.issuedAt} format="long" />,
      hideOnMobile: true,
    },
    {
      key: 'dueAt',
      header: 'Due',
      sortValue: (i) => i.dueAt,
      render: (i) => <DateCell value={i.dueAt} format="long" />,
    },
    {
      key: 'totalZAR',
      header: 'Total',
      sortValue: (i) => i.totalZAR,
      render: (i) => <MoneyCell value={i.totalZAR} />,
      align: 'right',
      width: 'w-36',
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (i) => i.status,
      render: (i) => <StatusCell tone={STATUS_TONE[i.status]} label={STATUS_LABEL[i.status]} />,
      width: 'w-32',
    },
    {
      key: 'actions',
      header: '',
      render: (i) => (
        <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
          <ActionsMenuCell
            ariaLabel={`Actions for ${i.number}`}
            items={[
              { label: 'View invoice', onSelect: () => navigate(`/app/billing/invoices/${i.id}`) },
              i.status === 'unpaid' || i.status === 'overdue'
                ? { label: 'Pay now', onSelect: () => navigate(`/app/billing/invoices/${i.id}`) }
                : null,
              {
                label: (
                  <span className="inline-flex items-center gap-1.5">
                    <Download className="size-3.5" aria-hidden />
                    Download PDF
                  </span>
                ),
                href: i.pdfUrl,
                disabled: !i.pdfUrl,
                separated: true,
              },
            ].filter(Boolean) as React.ComponentProps<typeof ActionsMenuCell>['items']}
          />
        </div>
      ),
      width: 'w-12',
      align: 'right',
    },
  ];

  return (
    <UniversalPage
      title="Invoices"
      description="All invoices issued to your account."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Billing', href: '/app/billing' },
        { label: 'Invoices' },
      ]}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav className="flex items-center gap-1" aria-label="Filter invoices">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={
                'rounded px-3 py-1.5 text-xs font-medium transition-colors ' +
                (filter === f.key
                  ? 'bg-ink text-background'
                  : 'bg-muted text-foreground-soft hover:bg-muted')
              }
            >
              {f.label}
            </button>
          ))}
        </nav>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Issued"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search invoice or quotation number"
            className="w-full sm:w-72"
          />
        </div>
      </div>

      <UniversalTable
        data={visible}
        columns={columns}
        getRowKey={(i) => i.id}
        onRowClick={(i) => navigate(`/app/billing/invoices/${i.id}`)}
        isLoading={isLoading}
        error={error}
        empty={{
          icon: Receipt,
          title:
            table.q || filter !== 'all' || table.filters.activeCount > 0
              ? 'No matching invoices'
              : 'No invoices yet',
          description:
            table.q || filter !== 'all' || table.filters.activeCount > 0
              ? 'Try a different filter, widen the period, or clear the search.'
              : 'Invoices appear here when services renew or add-ons are purchased.',
        }}
        caption={!isLoading && !error ? table.caption : undefined}
      />
    </UniversalPage>
  );
}
